<script>
  import jQuery from 'jquery';
  import { findIndex } from 'lodash-es';
  import { createEventDispatcher } from 'svelte';
  import Button from '@components/Button.svelte';
  import Badge from '@components/Badge.svelte';
  import MediaUpload from '@utils/media-upload';
  import Doka from '@utils/doka';
  import { getCurrentUserStore } from '@stores/current_user';
  import CreatorSpotlightAjax from '@ajax/creator_spotlight';
  import Sortable from 'sortablejs';

  const dispatch = createEventDispatcher();
  let user = getCurrentUserStore('default');

  export let mobile = false;
  export let hideBackButton = false;

  let galleryItems = [];
  let mapOrdered = [];
  let fileInput;
  let totalFilesUploaded = 0;
  let totalFilesProcessed = 0;
  let mediaSpotsRemaining;
  let availableSlots = 6;

  $: galleryItems = galleryItems.length ? galleryItems : [{ src: user.avatar_url, isProfile: true }];
  $: mediaSpotsRemaining = availableSlots - galleryItems.length;

  let loadUserCreatorSpotlightGalleryItems = async function () {
    let res = await CreatorSpotlightAjax.getSpotlightGalleryItems();
    galleryItems = res.gallery_items;
  };

  let removeFile = async function (id) {
    let res = await CreatorSpotlightAjax.deleteSpotlightGalleryItem(id);
    totalFilesUploaded += -1;
    totalFilesProcessed += -1;
    if (res.removed) {
      loadUserCreatorSpotlightGalleryItems();
    }
    return res;
  };

  let setOrder = async function (data) {
    let res = await CreatorSpotlightAjax.reorderSpotlightGalleryItems(data);
    loadUserCreatorSpotlightGalleryItems();
    return res;
  };

  let attachMediaUpload = function (node) {
    fileInput = node;

    let addFilesToGallery = async function (response) {
      galleryItems.push({
        content_url: response.uploadURL,
      });

      totalFilesProcessed += 1;

      if (totalFilesProcessed >= totalFilesUploaded) {
        await CreatorSpotlightAjax.createSpotlightGalleryItem({ content_url: response.uploadURL });
        loadUserCreatorSpotlightGalleryItems();
      }
    };

    let cropper = Doka().create({
      utils: ['crop'],
      cropAspectRatio: 1,
      allowButtonCancel: false,
    });

    MediaUpload.attach(
      fileInput,
      function (data, uppy) {
        for (var i = 0; i < data.length; i++) {
          var response = data[i];
          totalFilesUploaded = uppy.getFiles().length;
          addFilesToGallery(response);
        }
      },
      () => {},
      {
        maxNumberOfFiles: 1,
        note: 'Images up to 20 MB and video up to 200 MB',
        maxSize: {image: 20971520, video: 209715200},
        cropper: cropper,
        locale: {
          strings: {
            youCanOnlyUploadX: {
              1: 'Reached limit of %{smart_count} media items for this item',
            },
          },
        },
      }
    );
  };

  let uploadFile = function () {
    fileInput.click();
    totalFilesUploaded = 0;
    totalFilesProcessed = 0;
  };

  let sortOrderByIds = function () {
    let ids = [];
    jQuery('[data-image-id]').each(function () {
      ids.push(jQuery(this).data('image-id'));
    });

    return ids;
  };

  let sortFinished = function () {
    let ids = sortOrderByIds();

    mapOrdered = galleryItems
      .map((i) => {
        return {
          id: i.id,
          order: findIndex(ids, function (id) {
            return id == i.id;
          }),
        };
      })
      .sort((a, b) => (a.order > b.order ? 1 : -1));

    setOrder({ ordered_ids: mapOrdered.map((i) => i.id) });
  };

  let setupSortable = function (node) {
    new Sortable(node, {
      group: 'gallery',
      handle: '.photo-gallery-single',
      forceFallback: true,
      fallbackOnBody: true,
      filter: '.undraggable',
      fallbackTolerance: 5,
      scrollSensitivity: 120,
      scrollSpeed: 20,
      scroll: true,
      onStart: (evt) => {
        jQuery(evt.item).children('.photo-gallery-single').hide();
        jQuery(evt.item).children('.draggable-placeholder').show();
      },
      onSort: (evt) => {
        // If the user attempts to move the video to the first position
        if (jQuery(evt.item).children('.photo-gallery-single').hasClass('video-item') && evt.newIndex == 0) {
          // Move the item back to its original position
          evt.item.parentNode.insertBefore(evt.item, evt.item.parentNode.children[evt.oldIndex + 1]);
          return false; // Prevent sorting
        }

        sortFinished()
      },
      onEnd: (evt) => {
        jQuery(evt.item).children('.photo-gallery-single').show();
        jQuery(evt.item).children('.draggable-placeholder').hide();
      },
    });
  };

  loadUserCreatorSpotlightGalleryItems();
</script>

<style style="text/scss" lang="scss">
  @import '../../../vendor/design-system.scss';
  * :global(.draggable-placeholder) {
    border: 2px dashed #aaa;
    background: #eee;
    opacity: 0.5;
    height: 100%;
    width: 100%;
    border-radius: 20px;
    display: none;
    margin: 0;
  }

  * :global(.gallery-single-image),
  * :global(.gallery-single-video),
  * :global(.gallery-single-container) {
    max-width: 158px;
    aspect-ratio: 1 / 1;
    border-radius: 12px;
  }

  * :global(.sortable-chosen) {
    cursor: grabbing;
  }
  .photo-upload {
    .ui-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 24px;
    }
    .ui-title {
      font-weight: 500;
      font-size: 18px;
      line-height: 22px;
      margin-bottom: 8px;
    }
    .ui-subtitle {
      color: #717171;
    }
    .ui-back,
    .ui-close {
      background-color: transparent;
      border: none;
      margin: 0;
      padding: 0;
      opacity: 0.5;

      &:hover {
        opacity: 1;
      }
    }

    .photo-container {
      display: grid;
      gap: 12px;
      grid-template-columns: repeat(auto-fit, minmax(30%, 1fr));
      margin-bottom: 20px;
      cursor: grab;
    }

    .photo-upload-input-container,
    .photo-gallery-single {
      background: transparent;
      border: 2px solid #e3e3e3;
      border-radius: 20px;
      position: relative;
      padding: 12px 12px 24px;
    }

    .video {
      width: 100%;
    }

    .badge__profile {
      position: absolute;
      top: 23px;
      left: 20px;
    }

    .photo-upload-input-container {
      cursor: pointer;
    }

    .icon__add,
    .icon__remove {
      position: absolute;
      width: 36px;
      height: 36px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 999px;
      background-color: $neutral-black;
      top: 20px;
      right: 20px;
      border: 2px solid #e3e3e3;
      cursor: pointer;
      transition: 200ms;
      z-index: 1;

      &:hover {
        background-color: $modal-black;
      }
    }

    .icon__remove {
      background-color: $neutral-white;
      &:hover {
        background-color: #f1f1f1;
      }
    }

    .img_placeholder,
    .gallery-single-image,
    .gallery-single-container {
      display: flex;
      background: #f1f1f1;
      width: 100%;
      height: 100%;
      aspect-ratio: 1 / 1;
      border-radius: 12px;
      object-fit: cover;
      transition: 200ms;
    }

    .gallery-single-container {
      background: #000;
      align-items: center;
      justify-content: center;
    }

    .gallery-single-video {
      max-width: 100%;
      height: 100%;
    }

    .img_placeholder {
      border: 2px solid #e3e3e3;

      &:hover {
        background: darken(#ececec, 5%);
      }
    }
  }

  .mobile {
    .photo-container {
      grid-template-columns: repeat(auto-fit, minmax(45%, 1fr));
    }
  }
</style>

<div bind:this={fileInput} style="display: none" use:attachMediaUpload />

<div class="photo-upload" class:mobile={mobile}>
  <div class="ui-header">
    {#if hideBackButton}
      <div></div>
    {:else}
      <button
        class="ui-back"
        on:click={() => {
          dispatch('back');
        }}>
        <svg width="16" height="17" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M4.85501 7.26609L10.5901 1.49831L11.6538 2.55596L5.91867 8.32373C5.82171 8.42125 5.82171 8.57877 5.91867 8.67628L11.6538 14.4441L10.5901 15.5017L4.85501 9.73393C4.17626 9.05132 4.17626 7.94869 4.85501 7.26609Z"
            fill="#1C1C1C" />
        </svg>
      </button>
    {/if}
    <div>
      <div class="ui-title">Add photos or video</div>
      <div class="ui-subtitle">Drag to reorder. First spot is your profile photo.</div>
    </div>
    <button
      class="ui-close"
      on:click={() => {
        dispatch('close');
      }}
      ><svg width="16" height="17" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M6.93932 8.50003L2.00049 13.4386L3.06112 14.4993L8 9.56066L12.9389 14.4993L13.9995 13.4386L9.06069 8.50003L13.9995 3.56142L12.9389 2.50073L8 7.43939L3.06112 2.50073L2.00049 3.56142L6.93932 8.50003Z"
          fill="#1C1C1C" />
      </svg>
    </button>
  </div>

  <div class="photo-container" use:setupSortable>
    {#each galleryItems as item, index (item.content_url)}
      <div data-image-id={item.id}>
        <div class="draggable-placeholder">&nbsp;</div>
        <div class="photo-gallery-single" class:video-item={item.content_type == 'video'}>
          {#if index !== 0}
            <button class="icon__remove" on:click={removeFile(item.id)}>
              <svg width="16" height="17" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  fill-rule="evenodd"
                  clip-rule="evenodd"
                  d="M6.93932 8.50003L2.00049 13.4386L3.06112 14.4993L8 9.56066L12.9389 14.4993L13.9995 13.4386L9.06069 8.50003L13.9995 3.56142L12.9389 2.50073L8 7.43939L3.06112 2.50073L2.00049 3.56142L6.93932 8.50003Z"
                  fill="#1C1C1C" />
              </svg>
            </button>
          {/if}
          {#if index === 0}
          <div class="badge__profile">
            <Badge>
              <svelte:fragment slot="content">
                Profile
              </svelte:fragment>
            </Badge>
          </div>
          {/if}
          {#if (item.content_type == 'image')}
            <img alt="" class="gallery-single-image" src={item.content_url} />
          {:else}
            <div class="gallery-single-container">
              <video controls class="gallery-single-video" controlsList="nodownload">
                <source src={item.content_url} />
              </video>
            </div>
          {/if}
        </div>
      </div>
    {/each}
    {#each Array(mediaSpotsRemaining) as _}
      <button class="photo-upload-input-container" on:click={uploadFile}>
        <div class="icon__add">
          <svg width="16" height="17" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M7.25 9.18799V15.5H8.75V9.18799H15V7.68799H8.75V1.5H7.25V7.68799H1V9.18799H7.25Z"
              fill="white" />
          </svg>
        </div>
        <div class="img_placeholder"></div>
      </button>
    {/each}
  </div>
  <p class="ui-subtitle">Profiles with 6 items get better visibility.</p>
  <div class="finished-btn">
    <Button
      disabled={galleryItems.length < 3}
      colorScheme="black"
      style="padding-left: 40px; padding-right: 40px; width: auto; margin: 0;"
      text={galleryItems.length < 3 ? 'Add at least 3' : 'Continue'}
      on:click={() => {
        dispatch('finished');
      }} />
  </div>
</div>
